/** The name an image is saved under: the last path segment, as WebP. */
export function imageFileName(path: string): string {
    return `${path.substring(path.lastIndexOf('/') + 1)}.webp`;
}

/**
 * Image URLs point at our API, which needs the session cookie. A refused or missing image
 * throws rather than handing back the error page's bytes to be saved as an image.
 */
async function fetchImage(path: string): Promise<Response> {
    const response = await fetch(path, { credentials: 'include' });
    if (!response.ok) throw new Error(`Image request failed with status ${response.status}`);
    return response;
}

/**
 * The image's bytes, for the viewer's download menu. A one-off user action whose result is
 * saved straight away, so it is a plain request rather than a cached query; the viewer also
 * renders outside any query client in its tests and in exports.
 */
export async function fetchImageBlob(path: string): Promise<Blob> {
    return (await fetchImage(path)).blob();
}

/** One zip of every image, each named by `imageFileName`. */
export async function zipImages(paths: string[]): Promise<Blob> {
    const { zipSync } = await import('fflate');
    const entries = await Promise.all(
        paths.map(async (path) => {
            const bytes = new Uint8Array(await (await fetchImage(path)).arrayBuffer());
            return { name: imageFileName(path), bytes };
        })
    );
    const files: Record<string, Uint8Array> = {};
    for (const { name, bytes } of entries) files[name] = bytes;
    // WebP is compressed already, so the entries are stored as they are.
    return new Blob([zipSync(files, { level: 0 })], { type: 'application/zip' });
}
